import { useState, useMemo } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import { buildTree } from "./layouts";
import { initials, photoOf, bandColor, tenureLabel } from "@/lib/orgUtils";

export default function OutlineView({ employees, view, onSelect, focusNode }) {
  const { roots, childrenOf } = buildTree(employees, view?.root_employee_id);
  const [closed, setClosed] = useState(() => new Set());

  const rows = useMemo(() => {
    const out = [];
    const walk = (emp, depth) => {
      const kids = childrenOf[emp.employee_id] || [];
      out.push({ emp, depth, kids: kids.length });
      if (!closed.has(emp.id)) kids.forEach((k) => walk(k, depth + 1));
    };
    roots.forEach((r) => walk(r, 0));
    return out;
  }, [roots, childrenOf, closed]);

  const toggle = (id) =>
    setClosed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div className="h-full overflow-auto p-6" data-testid="outline-view">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-[#181B34]/10 shadow-sm p-4">
        {rows.map(({ emp, depth, kids }) => {
          const color = bandColor(emp.date_of_joining, view?.color_config);
          const isOpen = !closed.has(emp.id);
          return (
            <div
              key={emp.id}
              data-testid={`outline-row-${emp.employee_id}`}
              onClick={() => onSelect(emp)}
              className={`flex items-center gap-2 py-1.5 pr-3 rounded-lg cursor-pointer hover:bg-[#F0F3FF] ${focusNode?.id === emp.id ? "bg-[#6161FF]/10" : ""}`}
              style={{ paddingLeft: depth * 26 + 8, borderLeft: `4px solid ${color || "transparent"}` }}
            >
              {kids > 0 ? (
                <button onClick={(e) => { e.stopPropagation(); toggle(emp.id); }}
                  className="w-5 h-5 rounded flex items-center justify-center text-[#676a82] hover:bg-white">
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
              ) : (
                <span className="w-5" />
              )}
              {photoOf(emp) ? (
                <img src={photoOf(emp)} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <span className="w-7 h-7 rounded-full bg-[#F0F3FF] text-[#6161FF] flex items-center justify-center text-[10px] font-bold">{initials(emp.full_name)}</span>
              )}
              <span className="font-bold text-sm">{emp.full_name}</span>
              <span className="text-xs text-[#676a82] font-medium truncate">{emp.designation}</span>
              {emp.department && <span className="text-[10px] font-bold px-1.5 py-px rounded bg-[#579BFC]/15 text-[#2b6cb8]">{emp.department}</span>}
              {view?.color_config?.enabled && (
                <span className="ml-auto text-[11px] font-semibold text-[#676a82]">{emp.date_of_joining ? tenureLabel(emp.date_of_joining) : "DOJ missing"}</span>
              )}
              {kids > 0 && <span className="text-[10px] font-bold px-1.5 rounded-full bg-[#6161FF]/10 text-[#6161FF]">{kids}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
